import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { getCategoryTabId } from '@client/redux/modules/mylounge';
import { CategoryTabIds } from '@shared/constants';
import { getCookie } from '@pegasus/utils';
import { useLazyVariant } from '@client/lib/assignments';
import { getContinueShoppingData } from '@client/redux/modules/continueShopping';

import { ContinueShoppingDataType } from '../types';

export interface UseContinueShoppingReturn {
    shouldShowContinueShopping: boolean;
    continueShoppingData: ContinueShoppingDataType;
}

/**
 * Custom hook for managing Continue Shopping functionality
 *
 * @returns {UseContinueShoppingReturn} Object containing:
 * - shouldShowContinueShopping: Whether to show the Continue Shopping carousel on the All tab
 * - continueShoppingData: Data for the carousel (categories with products)
 */

const shouldDisplayCarousel = (
    isEnabled: boolean,
    tabId: string | null,
    continueShoppingDataLength: number,
): boolean => {
    return isEnabled && tabId === CategoryTabIds.ALL && continueShoppingDataLength > 0;
};

export const useContinueShopping = (): UseContinueShoppingReturn => {
    const categoryTabId = useSelector(getCategoryTabId);
    const continueShoppingData = useSelector(getContinueShoppingData);

    const [isVariantActive, sendFeedback] = useLazyVariant(
        'activate_continue_where_you_left',
        'variant_enable_continue_shop',
    );
    const isCookieEnabled = getCookie('isContinueShopping') === 'true';

    const isEnabled = useMemo(() => {
        return Boolean(isVariantActive || isCookieEnabled);
    }, [isVariantActive, isCookieEnabled]);

    const shouldShowContinueShopping = useMemo(() => {
        return shouldDisplayCarousel(isEnabled, categoryTabId, continueShoppingData?.length);
    }, [isEnabled, categoryTabId, continueShoppingData?.length]);

    useEffect(() => {
        if (shouldShowContinueShopping) {
            sendFeedback();
        }
    }, [shouldShowContinueShopping, sendFeedback]);

    return useMemo(
        () => ({
            shouldShowContinueShopping,
            continueShoppingData: (isEnabled
                ? continueShoppingData
                : []) as ContinueShoppingDataType,
        }),
        [shouldShowContinueShopping, continueShoppingData, isEnabled],
    );
};
